import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { FdicTopBar, GovBanner, FdicHeader, FdicFooter } from './fdicChrome';
import Step1RecordFound    from './Step1RecordFound';
import Step2Financials     from './Step2Financials';
import Step3CaseStatus     from './Step3CaseStatus';
import Step4TrackingForm   from './Step4TrackingForm';
import Step5TrackingResults from './Step5TrackingResults';

const FintechVerification = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [tracking, setTracking] = useState(null);
  const [completing, setCompleting] = useState(false);
  const navigate = useNavigate();

  // Load state + resume step
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/fintech/verification');
        if (!alive) return;

        // Already verified → straight to dashboard
        if (res.data.verified) {
          navigate('/fintech/dashboard', { replace: true });
          return;
        }

        setData(res.data);

        // Resume at saved step, but step 4 requires the tracking form to be
        // re-entered (we don't persist the correct tracking number input).
        const saved = res.data.currentStep || 1;
        setStep(saved >= 4 ? 4 : saved);
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  // Persist step changes (fire and forget)
  const persistStep = (next) => {
    setStep(next);
    api.patch('/fintech/verification/step', { step: next }).catch(() => {});
  };

  const finish = async () => {
    setCompleting(true);
    try {
      await api.post('/fintech/verification/complete');
      navigate('/fintech/dashboard', { replace: true });
    } catch (err) {
      console.error(err);
      setCompleting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f4f6f8]">
      <FdicTopBar />
      <GovBanner />
      <FdicHeader />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8">
          {loading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-[14px] text-gray-500">
              Loading your record…
            </div>
          )}

          {!loading && data && step === 1 && (
            <Step1RecordFound data={data} onNext={() => persistStep(2)} />
          )}
          {!loading && data && step === 2 && (
            <Step2Financials data={data} onNext={() => persistStep(3)} />
          )}
          {!loading && data && step === 3 && (
            <Step3CaseStatus data={data} onNext={() => persistStep(4)} />
          )}
          {!loading && data && step === 4 && (
            <Step4TrackingForm
              onSuccess={(payload) => {
                setTracking(payload);
                persistStep(5);
              }}
            />
          )}
          {!loading && data && step === 5 && (
            <Step5TrackingResults
              data={data}
              tracking={tracking}
              onComplete={finish}
              submitting={completing}
            />
          )}

          {!loading && !data && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-[14px] text-red-600">
              Could not load your verification record.
            </div>
          )}
        </div>
      </main>

      <FdicFooter />
    </div>
  );
};

export default FintechVerification;